import { produceWithPatches, applyPatches, enablePatches, type Patch } from "immer";
import type { ReportTemplate, Node } from "../types/template.js";
import { applyCommand, type Command } from "./commands.js";
import { indexTemplate, type NodeIndex, type NodeLocation, type RegionId } from "./tree.js";

enablePatches();

export interface HistoryEntry {
  label: string;
  patches: Patch[];
  inversePatches: Patch[];
  selectionBefore: string[];
  selectionAfter: string[];
  at: number;
  /** Consecutive entries with the same merge key within `mergeWindowMs` are coalesced. */
  mergeKey?: string;
}

export interface EditorState {
  template: ReportTemplate;
  selection: string[];
  hoverId: string | null;
  /** The node being edited inline (rich text), if any. */
  editingId: string | null;
  mode: "design" | "preview";
  zoom: number;
  activeRegion: RegionId;
  dirty: boolean;
  /** Monotonic counter incremented on every document change. */
  version: number;
}

export interface DispatchOptions {
  label?: string;
  /** Coalesce with the previous history entry sharing this key (typing, slider drags). */
  mergeKey?: string;
  /** Do not record in history (e.g. transient drag previews). */
  skipHistory?: boolean;
}

export interface EditorStoreOptions {
  template: ReportTemplate;
  maxHistory?: number;
  mergeWindowMs?: number;
  /** Called after every change, with the new template. */
  onChange?: (template: ReportTemplate, entry: HistoryEntry | null) => void;
}

type Listener = (state: EditorState, prev: EditorState) => void;

/**
 * Framework-agnostic editor store. The document is immutable; every change goes
 * through `dispatch(command)` and is recorded as immer patches for undo/redo.
 */
export class EditorStore {
  private state: EditorState;
  private undoStack: HistoryEntry[] = [];
  private redoStack: HistoryEntry[] = [];
  private listeners = new Set<Listener>();
  private txDepth = 0;
  private txPatches: Patch[] = [];
  private txInverse: Patch[] = [];
  private txLabel = "";
  private txSelectionBefore: string[] = [];
  private txSkipHistory = false;
  private indexCache: { version: number; index: NodeIndex } | null = null;
  /**
   * The history entry that was on top of the undo stack when the document was
   * last saved, so that undoing back to it reports clean again. Identity is the
   * whole test: undo pops, redo pushes, and a new edit pushes something else.
   */
  private savedEntry: HistoryEntry | null = null;
  /** A change that bypassed history, which no amount of undoing can take back. */
  private unhistoried = false;
  private readonly maxHistory: number;
  private readonly mergeWindowMs: number;
  private readonly onChange?: EditorStoreOptions["onChange"];

  constructor(opts: EditorStoreOptions) {
    this.state = {
      template: opts.template,
      selection: [],
      hoverId: null,
      editingId: null,
      mode: "design",
      zoom: 1,
      activeRegion: "body",
      dirty: false,
      version: 0
    };
    this.maxHistory = opts.maxHistory ?? 200;
    this.mergeWindowMs = opts.mergeWindowMs ?? 800;
    this.onChange = opts.onChange;
  }

  /* ----------------------------- reading ----------------------------- */

  getState(): EditorState {
    return this.state;
  }

  get template(): ReportTemplate {
    return this.state.template;
  }

  /** Cached id → node index for the current document version. */
  index(): NodeIndex {
    if (!this.indexCache || this.indexCache.version !== this.state.version) {
      this.indexCache = { version: this.state.version, index: indexTemplate(this.state.template) };
    }
    return this.indexCache.index;
  }

  getNode(id: string): Node | undefined {
    return this.index().get(id)?.node;
  }

  locationOf(id: string): NodeLocation | undefined {
    const e = this.index().get(id);
    return e ? { region: e.region, path: e.path } : undefined;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private setState(next: Partial<EditorState>) {
    const prev = this.state;
    this.state = { ...prev, ...next };
    for (const l of this.listeners) l(this.state, prev);
  }

  /* ------------------------------ UI state --------------------------- */

  select(ids: string[] | string | null): void {
    const selection = ids === null ? [] : Array.isArray(ids) ? ids : [ids];
    if (sameIds(selection, this.state.selection)) return;
    this.setState({ selection, editingId: selection.length === 1 && this.state.editingId === selection[0] ? this.state.editingId : null });
  }

  toggleSelect(id: string): void {
    const has = this.state.selection.includes(id);
    this.select(has ? this.state.selection.filter((s) => s !== id) : [...this.state.selection, id]);
  }

  hover(id: string | null): void {
    if (id !== this.state.hoverId) this.setState({ hoverId: id });
  }

  setEditing(id: string | null): void {
    if (id !== this.state.editingId) this.setState({ editingId: id, selection: id ? [id] : this.state.selection });
  }

  setMode(mode: EditorState["mode"]): void {
    if (mode !== this.state.mode) this.setState({ mode, editingId: null });
  }

  setZoom(zoom: number): void {
    const z = Math.min(3, Math.max(0.25, Math.round(zoom * 100) / 100));
    if (z !== this.state.zoom) this.setState({ zoom: z });
  }

  setActiveRegion(region: RegionId): void {
    if (region !== this.state.activeRegion) this.setState({ activeRegion: region });
  }

  markClean(): void {
    this.savedEntry = this.undoStack[this.undoStack.length - 1] ?? null;
    this.unhistoried = false;
    if (this.state.dirty) this.setState({ dirty: false });
  }

  /** Whether the document differs from the last saved state. */
  private isDirty(): boolean {
    if (this.unhistoried) return true;
    return (this.undoStack[this.undoStack.length - 1] ?? null) !== this.savedEntry;
  }

  /* ------------------------------ commands --------------------------- */

  /** Applies one command. Inside a transaction, patches accumulate into one history entry. */
  dispatch(command: Command, opts: DispatchOptions = {}): void {
    const [next, patches, inverse] = produceWithPatches(this.state.template, (draft) => {
      applyCommand(draft, command);
    });
    if (patches.length === 0) return;

    if (this.txDepth > 0) {
      this.txPatches.push(...patches);
      this.txInverse.unshift(...inverse);
      if (opts.skipHistory) this.txSkipHistory = true;
      this.commitTemplate(next, null);
      return;
    }

    const selectionBefore = this.state.selection;
    const entry: HistoryEntry = {
      label: opts.label ?? labelFor(command),
      patches,
      inversePatches: inverse,
      selectionBefore,
      selectionAfter: selectionBefore,
      at: Date.now(),
      mergeKey: opts.mergeKey
    };
    if (opts.skipHistory) this.unhistoried = true;
    else this.pushHistory(entry);
    this.commitTemplate(next, opts.skipHistory ? null : entry);
  }

  /**
   * Groups every dispatch inside `fn` into a single undo step. Nested
   * transactions join the outermost one. If `fn` throws, all changes roll back.
   */
  transaction<T>(label: string, fn: () => T): T {
    if (this.txDepth === 0) {
      this.txPatches = [];
      this.txInverse = [];
      this.txLabel = label;
      this.txSelectionBefore = this.state.selection;
      this.txSkipHistory = false;
    }
    this.txDepth++;
    const before = this.state.template;
    let result: T;
    try {
      result = fn();
    } catch (e) {
      this.txDepth--;
      if (this.txDepth === 0) {
        this.commitTemplate(before, null);
        this.txPatches = [];
        this.txInverse = [];
      }
      throw e;
    }
    this.txDepth--;
    if (this.txDepth === 0 && this.txPatches.length > 0) {
      const entry: HistoryEntry = {
        label: this.txLabel,
        patches: this.txPatches,
        inversePatches: this.txInverse,
        selectionBefore: this.txSelectionBefore,
        selectionAfter: this.state.selection,
        at: Date.now()
      };
      if (this.txSkipHistory) this.unhistoried = true;
      else this.pushHistory(entry);
      // The per-dispatch commits inside the transaction ran before the entry
      // existed, so the dirty flag they set was a guess. Settle it now.
      this.setState({ dirty: this.isDirty() });
      this.onChange?.(this.state.template, entry);
      this.txPatches = [];
      this.txInverse = [];
    }
    return result;
  }

  private commitTemplate(template: ReportTemplate, entry: HistoryEntry | null) {
    if (template === this.state.template) return;
    this.setState({ template, dirty: this.txDepth > 0 ? true : this.isDirty(), version: this.state.version + 1 });
    if (this.txDepth === 0) this.onChange?.(template, entry);
  }

  private pushHistory(entry: HistoryEntry) {
    const last = this.undoStack[this.undoStack.length - 1];
    if (entry.mergeKey && last && last.mergeKey === entry.mergeKey && entry.at - last.at < this.mergeWindowMs) {
      // Merging into the saved entry moves the document away from what was
      // saved while leaving the stack the same height, so the mark is gone.
      if (last === this.savedEntry) this.savedEntry = null;
      last.patches.push(...entry.patches);
      last.inversePatches.unshift(...entry.inversePatches);
      last.at = entry.at;
      last.selectionAfter = entry.selectionAfter;
    } else {
      this.undoStack.push(entry);
      if (this.undoStack.length > this.maxHistory) this.undoStack.shift();
    }
    this.redoStack = [];
  }

  /* ------------------------------ history ---------------------------- */

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  undoLabel(): string | undefined {
    return this.undoStack[this.undoStack.length - 1]?.label;
  }

  redoLabel(): string | undefined {
    return this.redoStack[this.redoStack.length - 1]?.label;
  }

  undo(): boolean {
    const entry = this.undoStack.pop();
    if (!entry) return false;
    const template = applyPatches(this.state.template, entry.inversePatches);
    this.redoStack.push(entry);
    this.setState({ template, selection: entry.selectionBefore.filter((id) => !!findIn(template, id)), editingId: null, dirty: this.isDirty(), version: this.state.version + 1 });
    this.onChange?.(template, null);
    return true;
  }

  redo(): boolean {
    const entry = this.redoStack.pop();
    if (!entry) return false;
    const template = applyPatches(this.state.template, entry.patches);
    this.undoStack.push(entry);
    this.setState({ template, selection: entry.selectionAfter.filter((id) => !!findIn(template, id)), editingId: null, dirty: this.isDirty(), version: this.state.version + 1 });
    this.onChange?.(template, null);
    return true;
  }

  /** Replaces the document and clears history (loading a template). */
  load(template: ReportTemplate): void {
    this.undoStack = [];
    this.redoStack = [];
    this.savedEntry = null;
    this.unhistoried = false;
    this.setState({ template, selection: [], hoverId: null, editingId: null, dirty: false, version: this.state.version + 1 });
  }

  history(): { undo: readonly HistoryEntry[]; redo: readonly HistoryEntry[] } {
    return { undo: this.undoStack, redo: this.redoStack };
  }
}

function findIn(template: ReportTemplate, id: string): Node | undefined {
  return indexTemplate(template).get(id)?.node;
}

function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

function labelFor(cmd: Command): string {
  switch (cmd.type) {
    case "insertNode":
    case "insertNodes":
      return "Insert";
    case "removeNodes":
      return "Delete";
    case "moveNode":
      return "Move";
    case "duplicateNode":
      return "Duplicate";
    case "setProps":
    case "setProp":
      return "Edit properties";
    case "setStyle":
      return "Edit style";
    case "setFlow":
      return "Edit page flow";
    case "setVisibleWhen":
      return "Edit visibility";
    case "setName":
      return "Rename";
    case "setLocked":
      return cmd.locked ? "Lock" : "Unlock";
    case "setRowLayout":
      return "Change columns";
    case "wrapNodes":
      return "Wrap";
    case "replaceNode":
      return "Replace";
    case "setPage":
      return "Page settings";
    case "setDocumentStyles":
    case "setTextStyle":
      return "Document styles";
    case "setTemplateName":
      return "Rename report";
    case "setSettings":
      return "Settings";
    case "setRegionFirstPage":
      return "First page header/footer";
    case "addLibraryItem":
      return "Save to library";
    case "removeLibraryItem":
      return "Remove from library";
    case "replaceTemplate":
      return "Load";
  }
}
